import {rootState} from '@mintdata-wsp/common/state/RootState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import {NonPassiveEventListener} from '@mintdata/mint-kit/impl/events/Events';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./SurfaceContainer.less');
const cx = classnames.bind(styles);

interface WheelDelta
{
    x:number;
    y:number;
}

@observer
export class SurfaceContainer extends BaseComponentWithContext<any, any>
{
    private _elementRef:HTMLElement = null;
    private _listenerDisposers:IDisposable[] = [];

    @bind
    private _onElementRef(elementRef:HTMLElement):void
    {
        this._elementRef = elementRef;
    }

    public componentDidMount():void
    {
        if( this._elementRef )
        {
            const listener = new NonPassiveEventListener(this._elementRef, 'wheel', this._onWheel);
            const disposer:IDisposable = this.context.nonPassiveEventListeners.add(listener);

            this._listenerDisposers.push(disposer);
        }
    }

    public componentWillUnmount():void
    {
        this._listenerDisposers.forEach(disposer => disposer.dispose());
    }

    public render():JSX.Element
    {
        const {surfaceArea} = controllers.ui;

        return (
            <div
                ref={this._onElementRef}
                className={cx('base', {isZenPreviewMode: surfaceArea.isInZenPreviewMode()})}
                data-surface-area="true"
            >
                {this.props.children}
            </div>
        );
    }

    @bind
    private _onWheel(event:WheelEvent):void
    {
        const {surfaceArea} = controllers.ui;
        const isPreviewMode:boolean = surfaceArea.isInPreviewOrZenPreviewMode();

        const delta:WheelDelta = {
            x: event.deltaX,
            y: event.deltaY
        }

        // Vertical Pan in previewMode
        if( event.metaKey && event.shiftKey && isPreviewMode )
        {
            this._pan(0, Math.sign(delta.x));
        }
        // Zoom
        else if( event.metaKey )
        {
            if( delta.y === 0 ) return;

            this._zoom(delta, event.pageX, event.pageY);

            event.preventDefault();
            event.stopPropagation();
        }
        // Horizontal Pan
        else if( event.shiftKey )
        {
            this._pan(delta.x, delta.y);
        }
        // Vertical or TouchPad Pan
        else if( !isPreviewMode )
        {
            if( !event.ctrlKey )
                this._pan(delta.x, delta.y);
            else
            {
                if( delta.y === 0 ) return;

                this._zoom(delta, event.pageX, event.pageY);

                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    private _zoom = (delta:WheelDelta, eventPageX:number, eventPageY:number) =>
    {
        const {surfaceArea} = controllers.ui;

        const pivot:Point = Point.from(eventPageX, eventPageY);

        if( delta.y < 0 )
            surfaceArea.zoomIn(pivot, Math.abs(delta.y));
        else
            surfaceArea.zoomOut(pivot, Math.abs(delta.y));
    }

    private _pan = (panXDirection:number, panYDirection:number) =>
    {
        const {surfaceArea} = controllers.ui;
        const panPosition = surfaceArea.getPanPosition();

        const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        const zoomRatio = 1 / zoom;

        const deltaX = Math.ceil(zoomRatio * panXDirection);
        const deltaY = Math.ceil(zoomRatio * panYDirection);

        surfaceArea.setPanPosition(Point.from(
            panXDirection ? panPosition.x - deltaX : panPosition.x,
            panYDirection ? panPosition.y - deltaY : panPosition.y
        ));
    }
}
